import {
    checkConstraintsAndThrowIfDosntValid,
    extractResultError,
    requestMessageTypeConstraints,
    resultError,
} from '@bpinc/ad-integration-api-utils'
import type {APIInstanceParams, APIPacket} from '@bpinc/adapters-public-api-types'
import {RESPONSE_SUFFIX, resultErrorCodes} from '@bpinc/adapters-public-api-types'

import {
    respondErrorToAPIAdapter,
    respondToAPIAdapter,
    subscribeToAPIAdapter,
    writeMessageToAPIAdapter,
} from './adapter-transport'
import {
    assertRequestMessage,
    isAPIInstanceParams,
    isCallbackMessageWithResponse,
    isCallbackResponseMessage,
    isRequestMessage,
    isValidAPIPacket,
} from './guards'
import type {InstanceResultPending} from './types'
import {createEmptyCallbackResponseCallbacksMap, getErrorMessage} from './utils'


const PING_TIMEOUT = 5000
const LIVE_INSTANCES_POLLING_INTERVAL = 60000
const LIVE_SIGNAL_TIMEOUT = 5000

export class IntegrationApiCoordinator {

    private readonly commWidgetFrame: HTMLIFrameElement

    /** Unique id of this Coordinator, stamped on every packet sent to the
     * Adapter(s). Lets an Adapter that shares the page with other Coordinators
     * (multi-instance) bind to a single Coordinator and ignore callback events
     * coming from the others - so each event is delivered once per Adapter. */
    private readonly coordinatorId = crypto.randomUUID()

    private readonly callbackResponseResolverMap = createEmptyCallbackResponseCallbacksMap()
    private readonly apiInstances: Record<string, APIInstanceParams> = {}

    private commWidgetReady = false

    /** True once the Comm.Widget has completed its handshake at least once.
     * Distinct from `commWidgetReady`, which is reset on ON_LOGOUT while the
     * widget iframe stays alive. Used to tell "widget never connected yet"
     * (queue requests) from "widget alive but logged out" (forward directly). */
    private commWidgetEverHandshaked = false

    private firstAPIInstanceParams: APIInstanceParams | undefined = undefined

    constructor() {
        this.commWidgetFrame = document.createElement('iframe')
        this.commWidgetFrame.id = 'communicator'
        const widgetPath = location.pathname.includes('/agent/crmembedded/no-ui')
            ? '/agent/crmembedded/no-ui/widget.html'
            : '/agent/crmembedded/widget.html'
        this.commWidgetFrame.src = location.origin + widgetPath + location.search

        document.body.appendChild(this.commWidgetFrame)

        this.registerAdapterListener()
        this.registerCommWidgetListener()
        this.startAdapterLookup()
        this.startMultiInstanceLivePolling()
    }

    private registerAdapterListener() {
        subscribeToAPIAdapter(packet => {
            if (!isValidAPIPacket(packet)) {
                return
            }

            if (packet.type === 'hi') {
                const initParams = packet.params[0]
                if (!isAPIInstanceParams(initParams)) {
                    console.error(
                        'API consumer instance incorrect initialization parameters:',
                        JSON.stringify(initParams),
                    )
                    return
                }
                /** Register new API instance */
                this.apiInstances[initParams.instanceId] = initParams

                /** Payload from the first "hi" message is used for state data.
                 * Subsequent "hi" messages, which can be received in multi-instance setup, will have no effect on state */
                if (!this.firstAPIInstanceParams) {
                    this.firstAPIInstanceParams = initParams
                }

                if (!this.firstAPIInstanceParams.integrationKey) {
                    console.error('API will not work without specifying integration key in the constructor')
                }
                writeMessageToAPIAdapter({
                    instanceId: 'broadcast',
                    uid: crypto.randomUUID(),
                    type: 'ready',
                    params: [],
                    coordinatorId: this.coordinatorId,
                })

                /** If the Comm.Widget already connected before this adapter
                 * registered (late/multi-instance adapter), tell it right away -
                 * the one-shot "comm-widget-ready" broadcast already fired. */
                if (this.commWidgetEverHandshaked) {
                    this.broadcastCommWidgetReady()
                }
                return
            }
            /** If no API instance completed handshake yet */
            if (!this.firstAPIInstanceParams?.integrationKey) {
                return
            }
            /** Received a message from API instance that doesn't respond for a while and was unregistered */
            if (!this.apiInstances[packet.instanceId]) {
                console.warn('Comm.Widget received message from unregistered API instance:', packet.type)
                return
            }
            try {
                if (isRequestMessage(packet.type)) {
                    this.propagateAdapterToCommWidgetRequest(packet)
                    return
                }
                if (isCallbackResponseMessage(packet.type)) {
                    this.propagateAdapterToCommWidgetCallbackResponse(packet)
                    return
                }
                respondErrorToAPIAdapter(
                    packet.instanceId,
                    packet.uid,
                    packet.type,
                    resultError(
                        'invalid_request',
                        'Incorrect message type, or this method is not yet implemented',
                    ),
                    this.coordinatorId,
                )
            } catch (e) {
                console.error('Error in message event listener:', getErrorMessage(e))
            }
        })
    }

    private registerCommWidgetListener() {
        window.addEventListener('message', ({data, origin, source}: MessageEvent) => {
            if (origin !== window.location.origin) {
                return
            }
            if (typeof data !== 'string') {
                return
            }
            if (process.env.NODE_ENV === 'development' && data.startsWith('webpackHotUpdate')) {
                return
            }
            /** Message from the login popup opened with the "openLogin" API method */
            if (data === 'reload') {
                window.location.reload()
                return
            }
            if (source !== this.commWidgetFrame.contentWindow) {
                return
            }
            let parsed = null
            try {
                parsed = JSON.parse(data)
            } catch (e) {
                return
            }
            if (!isValidAPIPacket(parsed)) {
                return
            }

            if (parsed.type === 'handshake' && this.firstAPIInstanceParams) {
                this.commWidgetReady = true
                this.commWidgetEverHandshaked = true

                const handshakeAnswer: APIPacket = {
                    instanceId: 'broadcast',
                    uid: parsed.uid,
                    type: 'handshake-answer',
                    params: [this.firstAPIInstanceParams],
                }
                this.postMessageToCommWidget(handshakeAnswer)

                /** Notify adapter(s) that the Comm.Widget is now connected and
                 * can service API requests (real readiness signal). */
                this.broadcastCommWidgetReady()
                return
            }

            if (parsed.type === 'ON_LOGOUT') {
                this.commWidgetReady = false
            }

            this.propagateCommWidgetToAdapterMessage(parsed)
        })
    }

    private startAdapterLookup() {
        const coordinatorId = this.coordinatorId

        function ping() {
            console.info('Broadcasting ping message in search for Integration API adapter...')
            writeMessageToAPIAdapter({
                instanceId: 'broadcast',
                type: 'hello',
                uid: crypto.randomUUID(),
                params: [],
                coordinatorId,
            })
        }
        ping()
        const intervalId = setInterval(() => {
            if (this.firstAPIInstanceParams?.integrationKey) {
                clearInterval(intervalId)
            } else {
                ping()
            }
        }, PING_TIMEOUT)
    }

    private startMultiInstanceLivePolling() {
        /** Every 60 seconds */
        setInterval(() => {
            /** If we have multiple API instances */
            const multiInstanceEnv = Object.values(this.apiInstances).length > 1
            if (!multiInstanceEnv) {
                return
            }

            /** Send "Who is alive?" message  */
            writeMessageToAPIAdapter({
                instanceId: 'broadcast',
                type: 'life-check',
                uid: crypto.randomUUID(),
                params: [],
            })

            /** Subscript to get answers */
            const detectedInstances: string[] = []
            const subscription = subscribeToAPIAdapter(data => {
                if (isValidAPIPacket(data) && data.type === 'amalive') {
                    detectedInstances.push(data.instanceId)
                }
            })

            /** Unsubscribe and commit collected instances */
            setTimeout(() => {
                subscription.unsubscribe()

                for (const instanceId of Object.keys(this.apiInstances)) {
                    if (!detectedInstances.includes(instanceId)) {
                        delete this.apiInstances[instanceId]
                    }
                }
            }, LIVE_SIGNAL_TIMEOUT)

        }, LIVE_INSTANCES_POLLING_INTERVAL)
    }

    private propagateAdapterToCommWidgetRequest(packet: APIPacket) {
        const {type, instanceId, uid, params} = packet
        try {
            assertRequestMessage(type)
            const paramsConstraint = requestMessageTypeConstraints[type]
            checkConstraintsAndThrowIfDosntValid(params, paramsConstraint)
        } catch (error) {
            const apiError = extractResultError(error)
            console.error(`Api error: ${type} ${uid}: ${JSON.stringify(apiError)}`)
            respondErrorToAPIAdapter(instanceId, uid, type, apiError, this.coordinatorId)
            return
        }

        if (this.commWidgetReady) {
            this.postMessageToCommWidget(packet)
        } else {
            this.handleNotAuthorizedRequest(packet)
        }
    }

    /** Previously we sent callback event (Comm.Widget --> Adapter instance(s))
     * Now we receive a response for it (Adapter instance --> Comm.Widget)  */
    private async propagateAdapterToCommWidgetCallbackResponse(packet: APIPacket) {
        const {type, uid, instanceId, params} = packet

        const pendingResolvers = this.callbackResponseResolverMap[type]
        if (!pendingResolvers?.[uid]) {
            console.warn('Comm.Widget received unexpected API response:', type, JSON.stringify(params))
            return
        }

        /** If a result is actual result, not default response when API instance has no handlers */
        if (!packet.noHandlers) {
            /** We're free to resolve/reject pending request without expecting other responses */
            this.postMessageToCommWidget(packet)
            delete pendingResolvers[uid]
            return
        }

        /** Register particular API instance response */
        pendingResolvers[uid].instances[instanceId] = {
            status: 'received',
            data: packet,
        }

        const resultFromAllInstancesReceived = Object.values(pendingResolvers[uid].instances)
            .every(instanceResult => instanceResult.status === 'received')
        if (resultFromAllInstancesReceived) {
            /** All API instances sent responses. And they all were "noHandlers" */
            this.postMessageToCommWidget(packet)
            pendingResolvers[uid].resolved = true
            delete pendingResolvers[uid]
            return
        }
    }

    /** Propagate messages from Comm.Widget to Adapter */
    private propagateCommWidgetToAdapterMessage(packet: APIPacket) {
        if (isCallbackMessageWithResponse(packet.type)) {
            const callbacks = this.callbackResponseResolverMap[packet.type + RESPONSE_SUFFIX]

            callbacks[packet.uid] = {
                resolved: false,
                instances: Object.keys(this.apiInstances)
                    .reduce<Record<string, InstanceResultPending>>((acc, instanceId) => {
                        acc[instanceId] = {
                            status: 'pending',
                            data: null,
                        } satisfies InstanceResultPending
                        return acc
                    }, {}),
            }
        }

        writeMessageToAPIAdapter({...packet, coordinatorId: this.coordinatorId})
    }

    private postMessageToCommWidget(packet: APIPacket) {
        this.commWidgetFrame.contentWindow?.postMessage(
            JSON.stringify(packet),
            window.location.origin,
        )
    }

    /** Broadcast to adapter instance(s) that the Comm.Widget has connected and
     * can service API requests. Mirrors "ready" (handshake #1) but signals
     * handshake #2 - actual Comm.Widget readiness. */
    private broadcastCommWidgetReady() {
        writeMessageToAPIAdapter({
            instanceId: 'broadcast',
            uid: crypto.randomUUID(),
            type: 'comm-widget-ready',
            params: [],
        })
    }

    private handleNotAuthorizedRequest(packet: APIPacket) {
        const {type, instanceId, uid} = packet
        switch (type) {
            case 'GET_LOGIN_STATE':
                respondToAPIAdapter(instanceId, uid, type, {
                    isLoggedIn: false,
                    agentId: undefined,
                    username: undefined,
                })
                break
            case 'OPEN_LOGIN': {
                const width = 550
                const height = 700
                const features = {
                    resizable: 'yes',
                    scrollbars: 'yes',
                    width,
                    height,
                    left: (window.screen.width - width) / 2,
                    top: (window.screen.height - height) / 2,
                }
                const featuresString = Object.entries(features)
                    .filter(([, value]) => value)
                    .map(([key, value]) => `${key}=${value}`)
                    .join(',')
                window.open(
                    '/agent/crmembedded/widget.html?autoclose=true',
                    '_blank',
                    featuresString,
                )
                respondToAPIAdapter(instanceId, uid, type, undefined)
                break
            }
            /** All other API methods unavailable in unauthorized state */
            default:
                respondErrorToAPIAdapter(instanceId, uid, type, {
                    code: resultErrorCodes.not_logged_in,
                    name: 'not_logged_in',
                    message: 'You are not logged in',
                }, this.coordinatorId)
        }
    }

}
